import { ToFlareXMLOptions } from '@common/flare/types/to-flare-xml-options.type';
import { nodeToFlareXML } from '@common/flare/util/prosemirror-flare-xml';
import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { nodeToDOM } from '@common/util/schema';
import { Node, NodeSpec } from 'prosemirror-model';

/**
 * Schema plugin for the catch-all unknown-tag parsers. Any HTML tag that doesn't have a more specific
 * schema node falls through to one of these — `unknown` for block contexts, `unknowninline` for inline
 * contexts. Both store the entire tag's outerHTML in the `content` attr and re-emit it verbatim on export.
 */
export class UnknownSchemaPlugin extends SchemaPlugin {
  nodes = {
    /** <unknown> is the catch-all parser for HTML tags that don't have a specific schema node. Block-context variant. */
    unknown: {
      group: 'block',
      attrs: {
        content: { default: undefined, skipExport: true, skipEditorExport: true },
        tagName: { default: undefined, skipExport: true, skipEditorExport: true }
      },
      parseDOM: [{
        tag: '*',
        priority: 0,
        getAttrs(dom: HTMLElement) {
          return {
            content: dom.outerHTML,
            tagName: dom.nodeName.includes(':') ? dom.nodeName : dom.nodeName.toLowerCase()
          };
        }
      }],
      toDOM(node) { return nodeToDOM(node); },
      toFlareXML(node: Node, options: ToFlareXMLOptions): string {
        return nodeToFlareXML(node, options, null, {
          xml: node.attrs.content
        });
      },
      toDisplayName(node) {
        return node.attrs.tagName ?? 'unknown';
      },
      toSourceTagName(node) {
        return node.attrs.tagName;
      },
      tagName: 'unknown',
      editorTagName: 'mc-lwe-unknown',
      isTagNameBox: true,
      noStyles: true,
      specContext: 'div'
    },

    /** <unknowninline> is the catch-all parser for HTML tags that don't have a specific schema node. Inline-context variant. */
    unknowninline: {
      group: 'inline',
      inline: true,
      attrs: {
        content: { default: undefined, skipExport: true, skipEditorExport: true },
        tagName: { default: undefined, skipExport: true, skipEditorExport: true }
      },
      parseDOM: [{
        tag: '*',
        priority: 1,
        context: 'block/',
        getAttrs(dom: HTMLElement) {
          return {
            content: dom.outerHTML,
            tagName: dom.nodeName.includes(':') ? dom.nodeName : dom.nodeName.toLowerCase()
          };
        }
      }],
      toDOM(node) { return nodeToDOM(node); },
      toFlareXML(node: Node, options: ToFlareXMLOptions): string {
        return nodeToFlareXML(node, options, null, {
          xml: node.attrs.content
        });
      },
      toDisplayName(node) {
        return node.attrs.tagName ?? 'unknown';
      },
      toSourceTagName(node) {
        return node.attrs.tagName;
      },
      tagName: 'unknown',
      editorTagName: 'mc-lwe-unknowninline',
      isTagNameBox: true,
      noStyles: true,
      specContext: 'paragraph'
    }
  } satisfies Record<string, NodeSpec>;
}
